import { v4 as uuidv4 } from 'uuid';
import { useAuth } from 'react-oidc-context';
import {
  Account,
  BasketItem,
  BasketMeta,
  BasketResponse,
  BasketSection,
  OrderResponse,
  GenericErrorResponse,
  QuoteExtras,
  QuoteExtra,
  BasketQuote,
  QuoteAddress,
  Price,
  ValidationResponse,
  OrderValidation,
  DiscountCode,
  DateType,
  QuoteParcels,
  Customs,
  QuoteDates,
  QuoteExtrasCalculations
} from '~/lib/types/basket-types';
import { BasketContext } from '~/providers/basketContext';
import { useAnalytics, useTenant } from '@parcel2go/npm-p2g-app';
import { useCallback, useContext, useMemo } from 'react';
import { BASKET_API, CONTENT_API, QUOTE_API, VERSION } from '~/lib/constants';
import { BasketSections, RequirementTypes } from '~/lib/types/enums';
import { getBasketItemQuotes, loadBasket, notifyBasketUpdate, storeBasketQuote } from '~/lib/basket';
import { authenticatedFetchWithTenantAnalytics } from '~/lib/fetch';
import { captureException, captureWarning, publishEvent } from '~/lib/metrics';
import {
  filterQuoteDatesWithExtraPrices,
  filterQuoteDatesWithoutExtraPrices,
  getQuotesValidCollectionDates,
  quoteToProtectionOptions
} from '~/lib/quotes';
import { onlyUnique, onlyUniqueNonBlank } from '~/lib/utils/array';
import { CompanyStatus } from '~/lib/types/customs';
import { getCookieValues, parseAbTastyCookie } from '~/lib/analytics';
import { ProtectionPrefix } from '~/lib/types/extra-types';
import { getFingerprint } from '~/lib/fingerprint';
import { useShopRequirements } from './use-shop-requirements';

/**
 * A hook used to work with the full basket.
 */
export default function useBasket() {
  const analytics = useAnalytics();
  const { tenant, settings } = useTenant();
  const { state, dispatch } = useContext(BasketContext);
  const { user } = useAuth();
  const destinationShopRequirements = useShopRequirements(RequirementTypes.DESTINATION);

  const loading = useMemo(() => {
    return !state.initialised || state.saving || state.fetching;
  }, [state.fetching, state.initialised, state.saving]);

  const items = useMemo(() => {
    if (!state.initialised) {
      return {};
    }

    if (!state.basket.items) {
      return {};
    }

    return state.basket.items;
  }, [state.initialised, state.basket.items]);

  const item: BasketItem = useMemo(() => {
    if (!state.initialised) {
      return { sections: {} };
    }

    if (!state.basket.items) {
      return { sections: {} };
    }

    return state.basket.items[state.currentItem] || { sections: {} };
  }, [state.initialised, state.basket.items, state.currentItem]);

  const validation: {
    errors: GenericErrorResponse | null;
    breakdown: OrderValidation | null;
    validating: boolean;
    warnings: GenericErrorResponse[];
  } = useMemo(() => {
    return {
      errors: state.validationErrors,
      breakdown: state.order,
      validating: state.validating,
      warnings: state.validationWarnings
    };
  }, [state.order, state.validating, state.validationErrors, state.validationWarnings]);

  const timeTaken = useMemo(() => {
    const section = item.sections[BasketSections.META];
    if (section && section.payload) {
      const meta = section.payload as BasketMeta;
      return new Date().getTime() - new Date(meta.created).getTime();
    }
  }, [item]);

  const count = useMemo(() => {
    if (!state.initialised) {
      return 0;
    }

    if (!state.basket.items) {
      return 0;
    }

    return Object.entries(items).filter(([, item]) => {
      return (item.sections[BasketSections.META]?.payload as BasketMeta)?.completed === true;
    }).length;
  }, [state.initialised, state.basket.items, items]);

  const discountCode = useMemo(() => {
    if (state.basket.items[BasketSections.DISCOUNT]?.sections[BasketSections.DISCOUNT]) {
      const section = state.basket.items[BasketSections.DISCOUNT]?.sections[BasketSections.DISCOUNT] as BasketSection;
      const discount = section?.payload as DiscountCode;
      return discount?.code;
    }
  }, [state.basket.items]);

  const rebuild = useCallback(async () => {
    const { basket, id, itemId } = await loadBasket(tenant, settings, analytics.session, user);
    if (!basket) {
      dispatch({ type: 'INIT_BASKET', basket: { id: id, items: {} } });
      return;
    }

    dispatch({
      type: 'INIT_BASKET',
      basket: basket,
      currentItem: itemId && basket.items[itemId] ? itemId : Object.keys(basket.items)[0]
    });
  }, [dispatch, analytics.session, settings, tenant, user]);

  const removeDiscountCode = useCallback(async () => {
    const response = await authenticatedFetchWithTenantAnalytics(
      user,
      tenant,
      settings,
      analytics.session,
      `${BASKET_API}/${tenant}/basket/${state.basket.id}/${BasketSections.DISCOUNT}`,
      {
        method: 'DELETE',
        body: JSON.stringify(item),
        headers: {
          'Content-Type': 'application/json'
        }
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to remove discount code invalid http response code ${response.status}`);
    }

    dispatch({ type: 'REMOVE_ITEM', id: BasketSections.DISCOUNT });
    state.events.emit('basket_item_removed', { id: BasketSections.DISCOUNT });
    state.broadcast.postMessage({ type: 'basket_item_removed', id: BasketSections.DISCOUNT });
  }, [dispatch, item, analytics.session, settings, state.basket.id, state.broadcast, state.events, tenant, user]);

  const validateBasket = useCallback(async () => {
    dispatch({ type: 'UPDATE_ORDER_VALIDATING', validating: true });
    const response = await authenticatedFetchWithTenantAnalytics(
      user,
      tenant,
      settings,
      analytics.session,
      `${QUOTE_API}/checkout/validate/basket/${state.basket.id}`,
      {
        method: 'POST'
      }
    );

    if (response.status === 404) {
      captureWarning('Basket not found', { section: 'checkout' });
      dispatch({
        type: 'UPDATE_ORDER_VALIDATION_ERRORS',
        errors: {
          detail: 'Basket not found',
          issues: [],
          status: 404,
          title: 'Basket not found',
          type: 'basket_not_found'
        }
      });
      await rebuild();
      return;
    }

    if (response.status === 400) {
      const errors = (await response.json()) as GenericErrorResponse;
      if (
        errors.issues &&
        errors.issues.length &&
        errors.issues.some((x) => x.type.indexOf('discountcode-unavailable') >= 0)
      ) {
        await removeDiscountCode();
        dispatch({ type: 'UPDATE_ORDER_VALIDATING', validating: false });
        return;
      }

      captureWarning('Checkout validation error', { section: 'checkout' });
      dispatch({ type: 'UPDATE_ORDER_VALIDATION_ERRORS', errors: errors });
      return;
    }

    if (!response.ok) {
      captureWarning('Checkout validation exception', { section: 'checkout' });
      dispatch({
        type: 'UPDATE_ORDER_VALIDATION_ERRORS',
        errors: {
          detail: 'Unknown error',
          issues: [],
          status: 500,
          title: 'Unknown error',
          type: 'unknown_checkout_error'
        }
      });
      return;
    }

    const result = (await response.json()) as ValidationResponse;
    if (result?.success && result.result) {
      dispatch({ type: 'UPDATE_ORDER_VALIDATION', breakdown: result.result, warnings: result.warnings || [] });
      return;
    }
  }, [dispatch, removeDiscountCode, analytics.session, settings, state.basket.id, tenant, user]);

  const validateBasketItemNoStore = useCallback(
    async (additionalExtras?: string[]) => {
      const item = items[state.currentItem];

      const o = item.sections[BasketSections.ORIGIN]?.payload as QuoteAddress;
      const d = item.sections[BasketSections.DESTINATION]?.payload as QuoteAddress;
      const customs = item.sections[BasketSections.CUSTOMS]?.payload as Customs;
      const customer = item.sections[BasketSections.ACCOUNT]?.payload as Account;
      const dates = item.sections[BasketSections.DATES]?.payload as QuoteDates;
      const parcels = item.sections[BasketSections.PARCELS]?.payload as QuoteParcels;
      const quote = item.sections[BasketSections.QUOTE]?.payload as BasketQuote;
      const extras = item.sections[BasketSections.EXTRAS]?.payload as QuoteExtras;

      const model = {
        ref: state.currentItem,
        customer: { ...customer },
        billingAddress: { useAddress: { ...o } },
        items: [
          {
            $type: 'shipment',
            ref: state.currentItem,
            serviceSlug: quote?.service.slug,
            collectionDate: dates.dates[0]?.date,
            origin: { useAddress: { ...o } },
            destination: { useAddress: { ...d } },
            parcels: [...parcels.parcels],
            extras: extras.extras
              .map((e) => e.key)
              .concat(additionalExtras || [])
              .filter(onlyUnique)
              .map((e) => ({ Key: e })),
            customs: { ...customs }
          }
        ]
      };

      const response = await authenticatedFetchWithTenantAnalytics(
        user,
        tenant,
        settings,
        analytics.session,
        `${QUOTE_API}/checkout/validate`,
        {
          headers: {
            'Content-Type': 'application/json'
          },
          method: 'POST',
          body: JSON.stringify(model)
        }
      );

      if (!response.ok) {
        const errors = (await response.json()) as GenericErrorResponse;
        return { type: 'UPDATE_ORDER_VALIDATION_ERRORS', errors: errors };
      }

      const result = (await response.json()) as ValidationResponse;
      if (result?.success && result.result) {
        return { type: 'UPDATE_ORDER_VALIDATION', breakdown: result.result, warnings: result.warnings || [] };
      }
    },
    [analytics.session, settings, state.currentItem, tenant, user, items]
  );

  const copyAccountToOrigin = (
    _state: { [name: string]: BasketSection },
    _updated: { [name: string]: BasketSection }
  ) => {
    const account = _updated[BasketSections.ACCOUNT]?.payload as Account;
    const origin = _updated[BasketSections.ORIGIN] || {
      ..._state[BasketSections.ORIGIN]
    };
    if (account && origin && !(origin.payload as QuoteAddress).email && !(origin.payload as QuoteAddress).contactname) {
      (origin.payload as QuoteAddress).email = account.email;
      (origin.payload as QuoteAddress).contactname = account.forename + ' ' + account.surname;
      _updated[BasketSections.ORIGIN] = origin;
    }
  };

  const load = useCallback(async () => {
    if (!state.fetching) {
      try {
        dispatch({ type: 'UPDATE_LOADING', fetching: true, saving: false });

        const response = await authenticatedFetchWithTenantAnalytics(
          user,
          tenant,
          settings,
          analytics.session,
          `${BASKET_API}/${tenant}/basket/${state.basket.id}/${state.currentItem}`
        );

        if (!response.ok) {
          throw new Error(`Failed to load item invalid http response code ${response.status}`);
        }

        const result = (await response.json()) as BasketResponse;
        if (!result?.success || !result?.result?.items[state.currentItem]) {
          if (result?.error?.code === '401.3') {
            await rebuild();
            return;
          }
          throw new Error(`Failed to load item invalid api response ${result.error?.code}-${result.error?.message}`);
        }

        dispatch({ type: 'UPDATE_ITEM', id: state.currentItem, item: result.result?.items[state.currentItem] });
        state.events.emit('basket_item_fetched', {
          id: state.currentItem,
          item: result.result?.items[state.currentItem]
        });
        state.broadcast.postMessage({
          type: 'basket_item_fetched',
          id: state.currentItem,
          item: result.result?.items[state.currentItem]
        });
      } catch (e) {
        captureException(e, 'Failed to load item', { section: 'basket' });
      } finally {
        dispatch({ type: 'UPDATE_LOADING', fetching: false, saving: false });
      }
    }
  }, [
    state.fetching,
    state.basket.id,
    state.currentItem,
    state.events,
    state.broadcast,
    dispatch,
    user,
    tenant,
    settings,
    analytics.session,
    rebuild
  ]);

  const bulkUpdateSections = useCallback(
    (sections: { [name: string]: unknown }) => {
      dispatch({ type: 'UPDATE_SECTIONS_PAYLOAD', id: state.currentItem, sections: sections });

      for (const section of Object.getOwnPropertyNames(sections)) {
        state.events.emit('basket_section_saved', {
          id: state.currentItem,
          name: section,
          section: sections[section]
        });

        state.broadcast.postMessage({
          type: 'basket_section_saved',
          id: state.currentItem,
          section: section,
          payload: sections[section]
        });
      }
    },
    [dispatch, state.broadcast, state.currentItem, state.events]
  );

  const saveChanges = useCallback(
    async (requote?: boolean) => {
      try {
        if (state.changes.includes(BasketSections.ACCOUNT)) {
          try {
            analytics.identitify(
              state.basket.items[state.currentItem].sections[BasketSections.ACCOUNT].payload as Account
            );
          } catch (e) {
            captureException(e, 'Failed to identify account', { section: 'basket' });
          }
        }

        // Do we have any changes?
        if (state.changes.length) {
          dispatch({ type: 'UPDATE_LOADING', fetching: false, saving: true });

          const sectionsToUpdate: { [name: string]: BasketSection } = {};
          for (const section of state.changes) {
            sectionsToUpdate[section] = { ...state.basket.items[state.currentItem].sections[section] };
          }

          // Setup the collection address
          if (state.changes.includes(BasketSections.ACCOUNT)) {
            copyAccountToOrigin(state.basket.items[state.currentItem].sections, sectionsToUpdate);
          }

          const response = await authenticatedFetchWithTenantAnalytics(
            user,
            tenant,
            settings,
            analytics.session,
            `${BASKET_API}/${tenant}/basket/${state.basket.id}/${state.currentItem}`,
            {
              method: 'POST',
              body: JSON.stringify(sectionsToUpdate),
              headers: {
                'Content-Type': 'application/json'
              }
            }
          );

          if (!response.ok) {
            throw new Error(`Failed to save basket invalid http response code ${response.status}`);
          }

          const result = (await response.json()) as BasketResponse;
          if (!result?.success || !result.result?.items[state.currentItem]) {
            throw new Error(
              `Failed to save basket invalid api response ${result.error?.code}-${result.error?.message}`
            );
          }

          dispatch({ type: 'UPDATE_CHANGES', changes: [] });

          let updates = 0;
          const updatedItem = { ...state.basket.items[state.currentItem] };
          for (const section of Object.getOwnPropertyNames(result.result.items[state.currentItem].sections)) {
            const currentCheck = updatedItem.sections[section]?.check;
            const updatedCheck = result.result.items[state.currentItem].sections[section]?.check;
            if (currentCheck !== updatedCheck) {
              updates++;
              updatedItem.sections[section] = result.result.items[state.currentItem].sections[section];
            }
          }

          if (updates > 0) {
            dispatch({ type: 'UPDATE_ITEM', id: state.currentItem, item: updatedItem });
            state.events.emit('basket_item_saved', {
              id: state.currentItem,
              item: updatedItem
            });
            state.broadcast.postMessage({
              type: 'basket_item_saved',
              id: state.currentItem,
              item: updatedItem
            });
          }

          const previousQuote = state.basket.items[state.currentItem].sections[BasketSections.QUOTE]
            .payload as BasketQuote;

          if (requote) {
            const result = await getBasketItemQuotes(
              tenant,
              settings,
              state.basket.id,
              state.currentItem,
              analytics.session,
              user
            );
            if (result.errors?.length || result.quotes.length === 0) {
              publishEvent('Basket service no longer available', {
                section: 'basket'
              });

              const payload: BasketQuote = {
                ...previousQuote,
                errors: result.errors?.length
                  ? result.errors
                  : [
                      {
                        type: 'service_unavailable',
                        detail: 'The service is no longer available, please choose another service.',
                        title: 'Service Unavailable'
                      }
                    ]
              };

              dispatch({
                type: 'UPDATE_SECTION_PAYLOAD',
                id: state.currentItem,
                name: BasketSections.QUOTE,
                payload: payload
              });

              const updatedBasket = await storeBasketQuote(
                tenant,
                settings,
                user,
                analytics.session,
                state.basket.id,
                state.currentItem,
                {
                  ...state.basket.items[state.currentItem].sections[BasketSections.QUOTE],
                  payload: payload
                }
              );

              notifyBasketUpdate(state.currentItem, dispatch, state.broadcast, state.events, updatedBasket);

              return payload;
            }

            const quote: BasketQuote = result.quotes[0];

            if (result.etag && result.etag === previousQuote.etag) {
              return { ...quote, etag: result.etag, errors: undefined };
            }

            dispatch({
              type: 'UPDATE_SECTION_PAYLOAD',
              id: state.currentItem,
              name: BasketSections.QUOTE,
              payload: quote
            });

            dispatch({ type: 'UPDATE_PROTECTION', protection: quoteToProtectionOptions(quote) });

            const payload: BasketQuote = { ...quote, etag: result.etag, errors: undefined };

            // Update the quote on the basket
            const updatedBasket = await storeBasketQuote(
              tenant,
              settings,
              user,
              analytics.session,
              state.basket.id,
              state.currentItem,
              {
                ...state.basket.items[state.currentItem].sections[BasketSections.QUOTE],
                payload: payload
              }
            );

            notifyBasketUpdate(state.currentItem, dispatch, state.broadcast, state.events, updatedBasket);

            return payload;
          }

          return previousQuote;
        } else {
          return state.basket.items[state.currentItem].sections[BasketSections.QUOTE].payload as BasketQuote;
        }
      } catch (e) {
        captureException(e, 'Failed to save basket', { section: 'basket' });
        await load();
      } finally {
        dispatch({ type: 'UPDATE_LOADING', fetching: false, saving: false });
      }
    },
    [
      dispatch,
      analytics,
      settings,
      state.basket.id,
      state.basket.items,
      state.broadcast,
      state.changes,
      state.currentItem,
      state.events,
      tenant,
      user,
      load
    ]
  );

  const add = useCallback(
    async (sections?: { [name: string]: BasketSection }, staging?: boolean): Promise<string> => {
      let output = '';
      try {
        dispatch({ type: 'UPDATE_LOADING', fetching: false, saving: true });

        const data = sections ?? {};

        if (!data[BasketSections.META]) {
          let host: string | undefined = undefined;
          if (analytics.referrer) {
            try {
              host = new URL(analytics.referrer)?.hostname;
            } catch (e) {
              host = undefined;
            }
          }

          data[BasketSections.META] = {
            payload: {
              staging: !!staging,
              completed: false,
              created: new Date().toISOString(),
              metadata: {
                device: analytics.id,
                qs: analytics.qs ?? undefined,
                referrer: host,
                session: analytics.session,
                path: analytics.path ?? undefined,
                appVersion: VERSION,
                fp: await getFingerprint(),
                ...getCookieValues(['_ga']),
                ...parseAbTastyCookie()
              }
            } as BasketMeta
          };
        }

        const id = uuidv4();

        const response = await authenticatedFetchWithTenantAnalytics(
          user,
          tenant,
          settings,
          analytics.session,
          `${BASKET_API}/${tenant}/basket/${state.basket.id}/${id}`,
          {
            method: 'POST',
            body: JSON.stringify(data),
            headers: {
              'Content-Type': 'application/json'
            }
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to add item invalid http response code ${response.status}`);
        }

        const result = (await response.json()) as BasketResponse;
        if (!result?.success || !result?.result?.items[id]) {
          throw new Error(`Failed to add item invalid api response ${result.error?.code}-${result.error?.message}`);
        }

        dispatch({ type: 'ADD_ITEM', id: id, item: result.result?.items[id] });
        dispatch({ type: 'CHANGE_ITEM', item: id });
        state.events.emit('basket_item_added', { id: id, item: result.result?.items[id] });
        state.broadcast.postMessage({ type: 'basket_item_added', id: id, item: result.result?.items[id] });
        output = id;
      } catch (e) {
        captureException(e, 'Failed to add item', { section: 'basket' });
      } finally {
        dispatch({ type: 'UPDATE_LOADING', fetching: false, saving: false });
      }
      return output;
    },
    [dispatch, user, tenant, settings, analytics, state.basket.id, state.events, state.broadcast]
  );

  const duplicate = useCallback(
    async (id: string, incParcels: boolean): Promise<{ id: string; sections: { [name: string]: BasketSection } }> => {
      const parcels = incParcels
        ? (items[id].sections[BasketSections.PARCELS]?.payload as QuoteParcels)?.parcels || []
        : [
            {
              id: uuidv4(),
              quantity: 1,
              weight: null,
              length: null,
              width: null,
              height: null,
              value: null
            }
          ];
      const newItemSections: { [name: string]: BasketSection } = {
        ...items[id].sections,
        [BasketSections.DESTINATION]: {
          payload: {
            country: (items[id].sections[BasketSections.DESTINATION]?.payload as QuoteAddress)?.country || 'GBR',
            companyStatus: CompanyStatus.PERSONAL
          } as QuoteAddress
        },
        [BasketSections.PARCELS]: {
          payload: {
            ...(items[id].sections[BasketSections.PARCELS]?.payload as QuoteParcels),
            parcels: parcels,
            splitParcels: [],
            initialParcels: parcels.length
          } as QuoteParcels
        }
      };

      // Remove this as it needs to be created in the Add function
      delete newItemSections[BasketSections.META];

      // Delete the check property from the sections so the basket saves correctly
      Object.keys(newItemSections).forEach((section) => {
        delete newItemSections[section]?.check;
      });

      const newId = await add(newItemSections);

      return { id: newId, sections: newItemSections };
    },
    [add, items]
  );

  const split = useCallback(async (): Promise<{ id: string; sections: { [name: string]: BasketSection } }[]> => {
    // If the've already completed this item, or they havent said they want to do multi-destination, return.
    const intent = (item.sections[BasketSections.DESTINATION]?.payload as QuoteAddress)?.metadata?.sendToMultiple;
    if ((item.sections[BasketSections.META]?.payload as BasketMeta)?.completed || !intent) {
      return [];
    }

    const output: { id: string; sections: { [name: string]: BasketSection } }[] = [];
    const parcelsToCopy = (item.sections[BasketSections.PARCELS]?.payload as QuoteParcels)?.splitParcels ?? [];
    const parcels = (item.sections[BasketSections.PARCELS]?.payload as QuoteParcels)?.parcels ?? [];

    const defaultContentSummary =
      intent === 'split-different-contents'
        ? undefined
        : parcels.map((x) => x.contentSummary).filter(onlyUniqueNonBlank)[0];

    const defaultValue =
      intent === 'split-different-contents' ? undefined : parcels.filter((x) => x.value).map((x) => x.value)[0];

    if (parcelsToCopy.length > 0) {
      for (let i = 0; i < parcelsToCopy.length; i++) {
        const parcelsToInclude = { ...parcelsToCopy[i] };

        if (!parcelsToInclude.contentSummary && defaultContentSummary) {
          parcelsToInclude.contentSummary = defaultContentSummary;
        }

        if (!parcelsToInclude.value && defaultValue) {
          parcelsToInclude.value = defaultValue;
        }

        const newItemSections: { [name: string]: BasketSection } = {
          ...item.sections,
          [BasketSections.DESTINATION]: {
            payload: {
              country: (item.sections[BasketSections.DESTINATION]?.payload as QuoteAddress)?.country || 'GBR',
              companyStatus: CompanyStatus.PERSONAL
            } as QuoteAddress
          },
          [BasketSections.PARCELS]: {
            payload: {
              ...(item.sections[BasketSections.PARCELS]?.payload as QuoteParcels),
              parcels: [parcelsToInclude],
              splitParcels: [],
              initialParcels: 1
            } as QuoteParcels
          }
        };

        // Remove this as it needs to be created in the Add function
        delete newItemSections[BasketSections.META];

        // Delete the check property from the sections so the basket saves correctly
        Object.keys(newItemSections).forEach((section) => {
          delete newItemSections[section]?.check;
        });

        const newId = await add(newItemSections, true);

        output.push({ id: newId, sections: newItemSections });
      }
    }

    return output;
  }, [add, item]);

  const change = useCallback(
    (id: string, action?: string | null) => {
      if (state.currentItem === id && state.currentAction === action) return;
      dispatch({ type: 'CHANGE_ITEM', item: id, action: action });
      state.events.emit('basket_item_changed', { id: id });
      state.broadcast.postMessage({ type: 'basket_item_changed', id: id });
    },
    [dispatch, state.broadcast, state.currentAction, state.currentItem, state.events]
  );

  const save = useCallback(async () => {
    try {
      dispatch({ type: 'UPDATE_LOADING', fetching: false, saving: true });

      const response = await authenticatedFetchWithTenantAnalytics(
        user,
        tenant,
        settings,
        analytics.session,
        `${BASKET_API}/${tenant}/basket/${state.basket.id}/${state.currentItem}`,
        {
          method: 'POST',
          body: JSON.stringify(state.basket.items[state.currentItem].sections),
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to save basket invalid http response code ${response.status}`);
      }

      const result = (await response.json()) as BasketResponse;
      if (!result?.success || !result.result?.items[state.currentItem]) {
        throw new Error(`Failed to save basket invalid api response ${result.error?.code}-${result.error?.message}`);
      }

      dispatch({ type: 'UPDATE_ITEM', id: state.currentItem, item: result.result?.items[state.currentItem] });
      state.events.emit('basket_item_saved', {
        id: state.currentItem,
        item: result.result?.items[state.currentItem]
      });
      state.broadcast.postMessage({
        type: 'basket_item_saved',
        id: state.currentItem,
        item: result.result?.items[state.currentItem]
      });
    } catch (e) {
      captureException(e, 'Failed to save basket section', { section: 'basket' });
      await load();
    } finally {
      dispatch({ type: 'UPDATE_LOADING', fetching: false, saving: false });
    }
  }, [
    dispatch,
    analytics.session,
    settings,
    state.basket.id,
    state.basket.items,
    state.broadcast,
    state.currentItem,
    state.events,
    tenant,
    user,
    load
  ]);

  const remove = useCallback(
    async (id: string) => {
      try {
        dispatch({ type: 'UPDATE_LOADING', fetching: false, saving: true });

        const response = await authenticatedFetchWithTenantAnalytics(
          user,
          tenant,
          settings,
          analytics.session,
          `${BASKET_API}/${tenant}/basket/${state.basket.id}/${id}`,
          {
            method: 'DELETE'
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to remove item invalid http response code ${response.status}`);
        }

        const result = (await response.json()) as BasketResponse;
        if (!result?.success) {
          throw new Error(`Failed to remove item invalid api response ${result.error?.code}-${result.error?.message}`);
        }

        dispatch({ type: 'REMOVE_ITEM', id: id });
        state.events.emit('basket_item_removed', { id: id });
        state.broadcast.postMessage({ type: 'basket_item_removed', id: id });
      } catch (e) {
        captureException(e, 'Failed to remove item', { section: 'basket' });
        await load();
      } finally {
        dispatch({ type: 'UPDATE_LOADING', fetching: false, saving: false });
      }
    },
    [dispatch, analytics.session, settings, state.basket.id, state.broadcast, state.events, tenant, user, load]
  );

  const changeService = useCallback(
    async (quote: BasketQuote, newExtras?: string[]) => {
      try {
        dispatch({ type: 'UPDATE_LOADING', fetching: false, saving: true });

        const collectionDates = getQuotesValidCollectionDates(quote);
        const collectionDatesWithoutUpsell = collectionDates.filter(filterQuoteDatesWithoutExtraPrices);
        const collectionDatesWithUpsell = collectionDates.filter(filterQuoteDatesWithExtraPrices);
        const collectionDate = collectionDatesWithoutUpsell[0] || collectionDatesWithUpsell[0];

        const extras = state.basket.items[state.currentItem].sections[BasketSections.EXTRAS]?.payload as
          | QuoteExtras
          | undefined;

        // If the user has selected new extras, update the extras section to only include those protection extras
        if (newExtras && extras?.extras) {
          const protectionExtras = newExtras.filter((extraKey) => extraKey.startsWith(ProtectionPrefix));

          // Reset Quote Page Logic
          if (extras.initialExtras) {
            extras.initialExtras = quote.availableExtras
              .filter((e) => !e.key.startsWith(ProtectionPrefix) || protectionExtras.includes(e.key))
              .map((e) => ({ key: e.key, metadata: {} }));
          }

          if (protectionExtras.length === 0) {
            extras.extras = extras.extras.filter((extra) => !extra.key.startsWith(ProtectionPrefix));
          } else {
            extras.extras = extras.extras
              .filter((extra) => !extra.key.startsWith(ProtectionPrefix))
              .concat(
                quote.availableExtras
                  .filter((e) => protectionExtras.includes(e.key))
                  .map((e) => ({ key: e.key, metadata: {} }))
              );
          }
        }

        const mutatedExtras: QuoteExtra[] = (extras?.extras || []).filter((extra) =>
          quote.availableExtras.some((a) => a.key === extra.key)
        );

        const initialExtras: QuoteExtra[] = (extras?.initialExtras || []).filter((extra) =>
          quote.availableExtras.some((a) => a.key === extra.key)
        );

        if (collectionDate && collectionDate.price && collectionDate.extraKey) {
          mutatedExtras.push({ key: collectionDate.extraKey, metadata: {} });
        }

        const originShop =
          quote.service.dropOffProviderGroup ===
          (item.sections[BasketSections.QUOTE]?.payload as BasketQuote).service.dropOffProviderGroup
            ? { ...(item.sections[BasketSections.ORIGIN].payload as QuoteAddress).shop }
            : undefined;

        const destinationShop =
          destinationShopRequirements.shopRequirement?.metadata?.ProviderGroup ===
          (item.sections[BasketSections.QUOTE]?.payload as BasketQuote).service.dropOffProviderGroup
            ? { ...(item.sections[BasketSections.DESTINATION].payload as QuoteAddress).shop }
            : undefined;

        const sections = {
          [BasketSections.QUOTE]: { ...item.sections[BasketSections.QUOTE], payload: quote },
          [BasketSections.SERVICE]: {
            ...item.sections[BasketSections.SERVICE],
            payload: { slug: quote.service.slug }
          },
          [BasketSections.EXTRAS]: {
            ...item.sections[BasketSections.EXTRAS],
            payload: {
              ...(item.sections[BasketSections.EXTRAS].payload as QuoteExtras),
              extras: mutatedExtras,
              initialExtras: initialExtras
            }
          },
          [BasketSections.EXTRAS_CALCULATIONS]: {
            ...item.sections[BasketSections.EXTRAS_CALCULATIONS],
            payload: {
              ...(item.sections[BasketSections.EXTRAS_CALCULATIONS].payload as QuoteExtrasCalculations),
              ddp: undefined
            }
          },
          [BasketSections.DESTINATION]: {
            ...item.sections[BasketSections.DESTINATION],
            payload: {
              ...(item.sections[BasketSections.DESTINATION].payload as QuoteAddress),
              shop: destinationShop,
              shopId: destinationShop?.id
            } as QuoteAddress
          },
          [BasketSections.ORIGIN]: {
            ...item.sections[BasketSections.ORIGIN],
            payload: {
              ...(item.sections[BasketSections.ORIGIN].payload as QuoteAddress),
              shop: originShop,
              shopId: originShop?.id
            } as QuoteAddress
          },
          [BasketSections.DATES]: {
            ...item.sections[BasketSections.DATES],
            payload: {
              dates:
                collectionDate && collectionDate.collection
                  ? [
                      {
                        type: DateType.Collection,
                        date: collectionDate.collection.date
                      }
                    ]
                  : []
            } as QuoteDates
          }
        };

        const response = await authenticatedFetchWithTenantAnalytics(
          user,
          tenant,
          settings,
          analytics.session,
          `${BASKET_API}/${tenant}/basket/${state.basket.id}/${state.currentItem}`,
          {
            method: 'POST',
            body: JSON.stringify(sections),
            headers: {
              'Content-Type': 'application/json'
            }
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to change service invalid http response code ${response.status}`);
        }

        const result = (await response.json()) as BasketResponse;
        if (!result?.success || !result.result?.items[state.currentItem]) {
          throw new Error(
            `Failed to change service invalid api response ${result.error?.code}-${result.error?.message}`
          );
        }

        notifyBasketUpdate(state.currentItem, dispatch, state.broadcast, state.events, result.result);
      } catch (e) {
        captureException(e, 'Failed to change service', { section: 'basket' });
        await load();
        return false;
      } finally {
        dispatch({ type: 'UPDATE_LOADING', fetching: false, saving: false });
      }
      return true;
    },
    [
      dispatch,
      item.sections,
      analytics.session,
      settings,
      state.basket.id,
      state.basket.items,
      state.broadcast,
      state.currentItem,
      state.events,
      tenant,
      user,
      load
    ]
  );

  const complete = useCallback(async () => {
    if (
      (state.basket.items[state.currentItem]?.sections[BasketSections.META]?.payload as BasketMeta)?.completed === true
    ) {
      return true;
    }

    try {
      dispatch({ type: 'UPDATE_LOADING', fetching: false, saving: true });

      const payload = item.sections[BasketSections.META]
        ? {
            ...item.sections[BasketSections.META],
            payload: {
              ...((item.sections[BasketSections.META].payload as BasketMeta) || {}),
              completed: true,
              staging: false
            }
          }
        : { payload: { completed: true, staging: false } };

      const response = await authenticatedFetchWithTenantAnalytics(
        user,
        tenant,
        settings,
        analytics.session,
        `${BASKET_API}/${tenant}/basket/${state.basket.id}/${state.currentItem}/${BasketSections.META}`,
        {
          method: 'POST',
          body: JSON.stringify(payload),
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to complete item invalid http response code ${response.status}`);
      }

      const result = (await response.json()) as BasketResponse;
      if (!result?.success || !result.result?.items[state.currentItem]?.sections[BasketSections.META]) {
        throw new Error(`Failed to complete item invalid api response ${result.error?.code}-${result.error?.message}`);
      }

      dispatch({
        type: 'UPDATE_SECTION',
        id: state.currentItem,
        name: BasketSections.META,
        section: result.result?.items[state.currentItem]?.sections[BasketSections.META]
      });

      state.events.emit('basket_section_saved', {
        id: state.currentItem,
        name: BasketSections.META,
        section: result.result?.items[state.currentItem]?.sections[BasketSections.META]
      });

      state.events.emit('basket_item_completed', {
        id: state.currentItem
      });

      state.broadcast.postMessage({
        type: 'basket_section_saved',
        id: state.currentItem,
        section: BasketSections.META,
        payload: result.result?.items[state.currentItem]?.sections[BasketSections.META]
      });

      return true;
    } catch (e) {
      captureException(e, 'Failed to complete item', { section: 'basket' });
      await load();
    } finally {
      dispatch({ type: 'UPDATE_LOADING', fetching: false, saving: false });
    }
    return false;
  }, [
    state.basket.items,
    state.basket.id,
    state.currentItem,
    state.events,
    state.broadcast,
    dispatch,
    item.sections,
    user,
    tenant,
    settings,
    analytics.session,
    load
  ]);

  const createOrder = useCallback(async () => {
    try {
      dispatch({ type: 'UPDATE_LOADING', fetching: false, saving: true });

      const response = await authenticatedFetchWithTenantAnalytics(
        user,
        tenant,
        settings,
        analytics.session,
        `${QUOTE_API}/checkout/basket/${state.basket.id}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.status === 400) {
        const errors = (await response.json()) as GenericErrorResponse;
        captureWarning('Failed to create order with validation errors', { section: 'checkout' });
        console.warn('Failed to create order with validation errors', errors);
        return {
          errors: errors,
          orderId: undefined,
          redirect: undefined,
          price: undefined,
          hash: undefined
        };
      }

      if (!response.ok) {
        throw new Error(`Failed to create order invalid http response code ${response.status}`);
      }

      const result = (await response.json()) as OrderResponse;
      if (!result?.success || !result.result?.orderId) {
        throw new Error(`Failed to create order invalid api response ${result.error?.code}-${result.error?.message}`);
      }

      if (window.location.href.indexOf('localhost') > -1) {
        return {
          redirect: `${CONTENT_API}/order/payment?id=${result.result.orderId}&hash=${result.result.hash}`,
          errors: undefined,
          orderId: result.result.orderId,
          hash: result.result.hash,
          price: {
            gross: result.result.total.gross,
            net: result.result.total.net,
            tax: result.result.total.tax
          } as Price
        };
      } else {
        return {
          redirect: `/order/payment?id=${result.result.orderId}&hash=${result.result.hash}`,
          errors: undefined,
          orderId: result.result.orderId,
          hash: result.result.hash,
          price: {
            gross: result.result.total.gross,
            net: result.result.total.net,
            tax: result.result.total.tax
          } as Price
        };
      }
    } catch (e) {
      captureException(e, 'Failed to create order', { section: 'basket' });
      return {
        redirect: undefined,
        price: undefined,
        orderId: undefined,
        hash: undefined,
        errors: {
          title: 'Failed to create order',
          type: 'create_order_error',
          status: 500
        } as GenericErrorResponse
      };
    } finally {
      dispatch({ type: 'UPDATE_LOADING', fetching: false, saving: false });
    }
  }, [dispatch, analytics.session, settings, state.basket.id, tenant, user]);

  const addDiscountCode = useCallback(
    async (discountCode: string) => {
      try {
        const item: BasketSection = state.basket.items[BasketSections.DISCOUNT]?.sections[BasketSections.DISCOUNT]
          ? {
              ...state.basket.items[BasketSections.DISCOUNT].sections[BasketSections.DISCOUNT],
              payload: {
                code: discountCode
              }
            }
          : {
              payload: {
                code: discountCode
              }
            };

        const response = await authenticatedFetchWithTenantAnalytics(
          user,
          tenant,
          settings,
          analytics.session,
          `${BASKET_API}/${tenant}/basket/${state.basket.id}/${BasketSections.DISCOUNT}/${BasketSections.DISCOUNT}`,
          {
            method: 'POST',
            body: JSON.stringify(item),
            headers: {
              'Content-Type': 'application/json'
            }
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to save section invalid http response code ${response.status}`);
        }

        const result = (await response.json()) as BasketResponse;
        if (!result?.success || !result.result?.items[BasketSections.DISCOUNT]?.sections[BasketSections.DISCOUNT]) {
          throw new Error(`Failed to save section invalid api response ${result.error?.code}-${result.error?.message}`);
        }

        dispatch({
          type: 'UPDATE_SECTION',
          id: BasketSections.DISCOUNT,
          name: BasketSections.DISCOUNT,
          section: result.result?.items[BasketSections.DISCOUNT]?.sections[BasketSections.DISCOUNT]
        });

        state.events.emit('basket_section_saved', {
          id: BasketSections.DISCOUNT,
          name: BasketSections.DISCOUNT,
          section: result.result?.items[BasketSections.DISCOUNT]?.sections[BasketSections.DISCOUNT]
        });
        state.broadcast.postMessage({
          type: 'basket_section_saved',
          id: BasketSections.DISCOUNT,
          section: BasketSections.DISCOUNT,
          payload: result.result?.items[BasketSections.DISCOUNT]?.sections[BasketSections.DISCOUNT]
        });
      } catch (e) {
        state.events.emit('basket_section_save_failed', {
          id: BasketSections.DISCOUNT,
          name: BasketSections.DISCOUNT,
          error: e
        });
        captureException(e, 'Error in useBasketSection save', {
          section: 'basket'
        });
      }
    },
    [
      dispatch,
      analytics.session,
      settings,
      state.basket.id,
      state.basket.items,
      state.broadcast,
      state.events,
      tenant,
      user
    ]
  );

  const upgradeProtection = useCallback(
    async (changes: { id: string; extra: string }[]) => {
      try {
        dispatch({ type: 'UPDATE_LOADING', fetching: false, saving: true });

        const basketUpdates: { [name: string]: { sections: { [name: string]: BasketSection } } } = {};
        for (const change of changes) {
          const extras = state.basket.items[change.id].sections[BasketSections.EXTRAS]?.payload as QuoteExtras;
          const mutatedExtras: QuoteExtra[] = (extras?.extras || []).filter(
            (extra) => !extra.key.startsWith(ProtectionPrefix)
          );
          mutatedExtras.push({ key: change.extra, metadata: {} });
          basketUpdates[change.id] = {
            sections: {
              [BasketSections.EXTRAS]: {
                ...state.basket.items[change.id].sections[BasketSections.EXTRAS],
                payload: {
                  ...(state.basket.items[change.id].sections[BasketSections.EXTRAS].payload as QuoteExtras),
                  extras: mutatedExtras
                }
              }
            }
          };
        }
        const response = await authenticatedFetchWithTenantAnalytics(
          user,
          tenant,
          settings,
          analytics.session,
          `${BASKET_API}/${tenant}/basket/${state.basket.id}`,
          {
            method: 'POST',
            body: JSON.stringify(basketUpdates),
            headers: {
              'Content-Type': 'application/json'
            }
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to save basket invalid http response code ${response.status}`);
        }

        const result = (await response.json()) as BasketResponse;
        if (!result?.success || !result.result?.items) {
          throw new Error(`Failed to save basket invalid api response ${result.error?.code}-${result.error?.message}`);
        }

        for (const item of Object.getOwnPropertyNames(result.result.items)) {
          notifyBasketUpdate(item, dispatch, state.broadcast, state.events, result.result);
        }
      } catch (e) {
        captureException(e, 'Failed to upgrade protection', { section: 'basket' });
      } finally {
        dispatch({ type: 'UPDATE_LOADING', fetching: false, saving: false });
      }
    },
    [
      analytics.session,
      dispatch,
      settings,
      state.basket.id,
      state.basket.items,
      state.broadcast,
      state.events,
      tenant,
      user
    ]
  );

  return {
    /** The Basket ID */
    id: state.basket.id,
    /** Current Basket Item Id */
    currentItemId: state.currentItem,
    /** The current basket item action */
    currentAction: state.currentAction,
    /** The single basket item that is marked as the current basket item */
    item,
    /** A collection of ALL of the basket items, indexed on the basket item ID */
    items,
    /** Function used to add a new basket item */
    add,
    /** Function used to change the current basket item */
    change,
    /** Function used to persist the whole basket */
    save,
    /** Function used to reload the whole basket from the server */
    load,
    /** A boolean stating if any saving or loading operations are currently in process for the basket */
    loading,
    /** The number of items in the basket that are marked as complete */
    count,
    /** Function used to remove a basket item */
    remove,
    /** Function used to duplicate a basket item by id */
    duplicate,
    /** Function used to split a basket item. This will create items for all parcels bar the first one in the array. */
    split,
    /** Function used to mark the current basket item as complete */
    complete,
    /** Function used to create an order from the current basket */
    createOrder,
    /** Function used to change the service of the current basket item */
    changeService,
    /** Function used to save the uncommited basket sections */
    saveChanges,
    /** Property to check if the basket state has been initialised*/
    initialised: state.initialised,
    /** Property to check if the basket is currently being re-quoted */
    quoting: state.requote,
    /** Change Key */
    changes: state.changes,
    /** Time taken to complete the current basket item */
    timeTaken,
    /** Function used to validate the basket */
    validateBasket,
    /** Validation state of the basket */
    validation,
    /** Function used to add a discount code to the basket */
    addDiscountCode,
    /** The discount code currently applied to the basket */
    discountCode,
    /** Function used to validate the current basket item */
    validateBasketItemNoStore,
    /** Function used to bulk save sections */
    bulkUpdateSections,
    /** True if the basket is currently being validated */
    validating: state.validating,
    /** Upgrade the protection to best for all items */
    upgradeProtection
  };
}
